import type React from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../hooks/useAppContext";
import { Card, CardContent, CardFooter } from "./ui/card";
import { CircularProgress } from "./custom/CircularScore";
import { PLAGIARISM_SCORE_THRESHOLDS } from "../services/plagiarism";
import { PlagiarismDescription } from "./custom/PlagiarismDescription";
import { PlagiarismSources } from "./custom/PlagiarismSources";
import { OriginalText } from "./custom/OriginalText";
import { Accordion, AccordionContent, AccordionItem } from "./ui/accordion";
import {
	AccordionTitleScoreWithTrigger,
	AccordionTitleWithTrigger,
} from "./custom/AccordionTitleWithTrigger";
import { DocumentTextIcon, LinkIcon } from "@heroicons/react/24/outline";
import { ResultPage } from "./custom/ResultPage";
import { trackEvent } from "../services/metrics";

const getScoreDescription = (score: number) => {
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.veryLikelyPlagiarism)
		return "Severe Plagiarism";
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.mostlyPlagiarism)
		return "High Plagiarism";
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.mixed) return "Moderate Plagiarism";
	if (score >= PLAGIARISM_SCORE_THRESHOLDS.mostlyOriginal)
		return "Low Plagiarism";
	return "Very Likely Original";
};

const ResultPlagiarism: React.FC = () => {
	const navigate = useNavigate();
	const { plagiarismResult } = useAppContext();
	const { score } = plagiarismResult;

	const handleBack = () => {
		navigate("/plagiarism");
		trackEvent({ event: "plagiarism_again_clicked" });
	};

	return (
		<ResultPage onPressBack={handleBack}>
			<Accordion
				type="multiple"
				className="w-full h-full overflow-y-scroll"
				defaultValue={["item-2", "item-3"]}
			>
				<AccordionItem value="item-1">
					<AccordionTitleWithTrigger
						title="Submitted Text"
						icon={<DocumentTextIcon className="size-4" />}
					/>

					<AccordionContent className="flex flex-col items-center">
						<OriginalText
							text={plagiarismResult.text}
							indexes={plagiarismResult.indexes}
						/>
					</AccordionContent>
				</AccordionItem>

				<AccordionItem value="item-2">
					<AccordionTitleScoreWithTrigger
						title="Plagiarism Score"
						score={plagiarismResult.score}
					/>
					<AccordionContent className="flex flex-col gap-4 text-balance">
						<Card className="mx-3">
							<CardContent className="flex flex-col gap-3">
								<div className="px-6 w-36 mx-auto">
									<CircularProgress value={score} />
								</div>
								<h2 className="text-lg font-semibold text-center pt-2">
									{getScoreDescription(score)}
								</h2>
							</CardContent>
							<CardFooter>
								<PlagiarismDescription value={score} />
							</CardFooter>
						</Card>
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="item-3">
					<AccordionTitleWithTrigger
						title="Matching Sources"
						icon={<LinkIcon className="size-4" />}
					/>
					<AccordionContent className="flex flex-col gap-4 text-balance">
						<PlagiarismSources sources={plagiarismResult.sources} />
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</ResultPage>
	);
};

export default ResultPlagiarism;
